import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImmunizationTracker = ({ immunizations = [] }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete':
        return 'CheckCircle2';
      case 'Due':
        return 'Clock';
      case 'Overdue':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return 'text-success';
      case 'Due':
        return 'text-warning';
      case 'Overdue':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  if (immunizations?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="Syringe" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-base font-medium text-foreground mb-2">No Immunization Records</p>
        <p className="text-sm text-muted-foreground mb-4">Patient immunization history will appear here</p>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Add Immunization
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">Immunization History</h3>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
          Add Record
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3">
        {immunizations?.map((imm) => (
          <div
            key={imm?.id}
            className="bg-card border border-border rounded-lg p-4 md:p-5 transition-smooth hover:shadow-elevation-2"
          >
            <div className="flex items-start gap-3 md:gap-4">
              <div className={`flex-shrink-0 ${getStatusColor(imm?.status)}`}>
                <Icon name={getStatusIcon(imm?.status)} size={24} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                  <h4 className="text-base md:text-lg font-semibold text-foreground">
                    {imm?.vaccine}
                  </h4>
                  <span className="text-xs md:text-sm text-muted-foreground whitespace-nowrap">
                    {imm?.date}
                  </span>
                </div>
                
                {imm?.lot && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded-md mb-3">
                    <Icon name="Package" size={14} />
                    <span className="text-xs md:text-sm font-mono text-foreground">Lot: {imm?.lot}</span>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                  {imm?.site && (
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={14} />
                      <span>Site: {imm?.site}</span>
                    </div>
                  )}
                  {imm?.route && (
                    <div className="flex items-center gap-2">
                      <Icon name="Activity" size={14} />
                      <span>Route: {imm?.route}</span>
                    </div>
                  )}
                  {imm?.administeredBy && (
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={14} />
                      <span>{imm?.administeredBy}</span>
                    </div>
                  )}
                  {imm?.nextDue && (
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={14} />
                      <span>Next: {imm?.nextDue}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImmunizationTracker;